import { useEffect, useState } from 'react';
import { Modal } from '@/deploy/components/ui/Modal';
import { Button } from '@/deploy/components/ui/Button';
import { networkService } from '@/deploy/services/network.service';
import type {
  Certificate, CertificateKind, DeploymentStep, KeyType, NetworkJob, ProjectDomain, TargetSummary,
} from '@/deploy/types/network';
import { StepsEditor, cleanSteps } from './StepsEditor';
import { errorMessage } from './networkFormat';
import styles from './Network.module.scss';

const KEY_TYPES: { value: KeyType; label: string }[] = [
  { value: 'EC256', label: 'ECDSA P-256 (recommandé)' },
  { value: 'EC384', label: 'ECDSA P-384' },
  { value: 'RSA2048', label: 'RSA 2048 (compatibilité)' },
  { value: 'RSA4096', label: 'RSA 4096' },
];

type OnJob = (job: NetworkJob, title: string) => void;

/** Création d'un certificat : externe (déployé par SSH) ou interne (wildcard pour plusieurs vhosts). */
export function CertificateCreateModal({ projectId, kind: initialKind, projectDomains, targets, defaultRenewBefore, onClose, onJob }: {
  projectId: string;
  kind: CertificateKind;
  projectDomains: ProjectDomain[];
  targets: TargetSummary[];
  defaultRenewBefore?: number;
  onClose: () => void;
  onJob: OnJob;
}) {
  const [kind, setKind] = useState<CertificateKind>(initialKind);
  const [domains, setDomains] = useState('');
  const [keyType, setKeyType] = useState<KeyType>('EC256');
  const [renewBefore, setRenewBefore] = useState('');
  const [steps, setSteps] = useState<DeploymentStep[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const list = domains.split(/[\s,]+/).map((d) => d.trim().toLowerCase()).filter(Boolean);
      if (list.length === 0) throw new Error('Au moins un domaine est requis');
      const job = await networkService.createCertificate(projectId, {
        kind,
        domains: list,
        keyType,
        renewBeforeDays: renewBefore ? Number(renewBefore) : null,
        ...(kind === 'EXTERNAL' ? { steps: cleanSteps(steps) } : {}),
      });
      onJob(job, `Nouveau certificat : ${list[0]}`);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Nouveau certificat" width="lg"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button>
        <Button variant="primary" onClick={submit} loading={busy}>
          {kind === 'EXTERNAL' && steps.length > 0 ? 'Créer et déployer' : 'Créer'}
        </Button>
      </>}>
      <div className={styles.form}>
        <div className={styles.formRow}>
          <label className={styles.field}>
            <span>Usage</span>
            <select value={kind} onChange={(e) => setKind(e.target.value as CertificateKind)}>
              <option value="EXTERNAL">Externe (déployé sur un serveur)</option>
              <option value="INTERNAL">Reverse proxy (ex : wildcard partagé)</option>
            </select>
          </label>
          <label className={styles.field}>
            <span>Type de clé</span>
            <select value={keyType} onChange={(e) => setKeyType(e.target.value as KeyType)}>
              {KEY_TYPES.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
          </label>
        </div>
        <label className={styles.field}>
          <span>Domaines</span>
          <textarea value={domains} onChange={(e) => setDomains(e.target.value)} rows={2} autoFocus
            placeholder={`mail.${projectDomains[0]?.name ?? 'exemple.fr'}\n*.${projectDomains[0]?.name ?? 'exemple.fr'}`} />
          <span className={styles.hint}>
            Un par ligne, wildcards acceptés. Validation par DNS (Cloudflare), sous : {projectDomains.map((d) => d.name).join(', ') || 'aucun domaine'}.
          </span>
        </label>
        <label className={styles.field}>
          <span>Renouveler avant expiration (jours)</span>
          <input type="number" min={1} max={60} value={renewBefore} onChange={(e) => setRenewBefore(e.target.value)}
            placeholder={defaultRenewBefore ? `${defaultRenewBefore} (défaut)` : 'défaut global'} />
        </label>
        {kind === 'EXTERNAL' && (
          <div className={styles.field}>
            <span>Procédure de déploiement</span>
            <StepsEditor steps={steps} targets={targets} onChange={setSteps} disabled={busy} />
          </div>
        )}
        {error && <div className={styles.error}>{error}</div>}
      </div>
    </Modal>
  );
}

/** Délai de renouvellement et type de clé (appliqué au prochain renouvellement). */
export function CertificateSettingsModal({ projectId, certificate, onClose, onSaved }: {
  projectId: string;
  certificate: Certificate;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [renewBefore, setRenewBefore] = useState(certificate.renewBeforeDays?.toString() ?? '');
  const [keyType, setKeyType] = useState<KeyType>(certificate.keyType);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await networkService.updateCertificate(projectId, certificate.id, renewBefore
        ? { renewBeforeDays: Number(renewBefore), keyType }
        : { resetRenewBeforeDays: true, keyType });
      onSaved();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Réglages du certificat" subtitle={certificate.name} width="sm"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button>
        <Button variant="primary" onClick={submit} loading={busy}>Enregistrer</Button>
      </>}>
      <div className={styles.form}>
        <label className={styles.field}>
          <span>Renouveler avant expiration (jours)</span>
          <input type="number" min={1} max={60} value={renewBefore} onChange={(e) => setRenewBefore(e.target.value)}
            placeholder={`${certificate.effectiveRenewBeforeDays} (défaut global)`} />
          <span className={styles.hint}>Vide : défaut global. Pris en compte dès la prochaine vérification (toutes les heures).</span>
        </label>
        <label className={styles.field}>
          <span>Type de clé</span>
          <select value={keyType} onChange={(e) => setKeyType(e.target.value as KeyType)}>
            {KEY_TYPES.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
          <span className={styles.hint}>Appliqué au prochain renouvellement.</span>
        </label>
        {error && <div className={styles.error}>{error}</div>}
      </div>
    </Modal>
  );
}

/** Procédure de déploiement d'un certificat externe existant. */
export function StepsModal({ projectId, certificate, targets, onClose, onJob }: {
  projectId: string;
  certificate: Certificate;
  targets: TargetSummary[];
  onClose: () => void;
  onJob: OnJob;
}) {
  const [steps, setSteps] = useState<DeploymentStep[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    networkService.getSteps(projectId, certificate.id).then(setSteps).catch((e) => setError(errorMessage(e)));
  }, [projectId, certificate.id]);

  const save = async (deploy: boolean) => {
    if (!steps) return;
    setBusy(true);
    setError(null);
    try {
      setSteps(await networkService.setSteps(projectId, certificate.id, cleanSteps(steps)));
      if (deploy) {
        onJob(await networkService.deployCertificate(projectId, certificate.id), `Déploiement : ${certificate.name}`);
        return;
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Procédure de déploiement" subtitle={certificate.name} width="lg"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={busy}>Fermer</Button>
        <Button variant="secondary" onClick={() => void save(false)} loading={busy} disabled={!steps}>Enregistrer</Button>
        <Button variant="primary" onClick={() => void save(true)} disabled={busy || !steps || steps.length === 0}>
          Enregistrer et déployer
        </Button>
      </>}>
      <div className={styles.form}>
        <span className={styles.hint}>Exécutée après chaque renouvellement, étape par étape, sur une connexion SSH par serveur.</span>
        {steps === null && !error ? <div className={styles.muted}>Chargement…</div> : null}
        {steps && <StepsEditor steps={steps} targets={targets} onChange={setSteps} disabled={busy} />}
        {saved && <div className={styles.info}>Procédure enregistrée.</div>}
        {error && <div className={styles.error}>{error}</div>}
      </div>
    </Modal>
  );
}
